<!-- @format -->
<template>
    <div class="widget landing-page-template-select-widget">
        <div class="widget-header">
            <h3>Choose Template</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <p>Select an existing page</p>
            <selector-table
                v-model="selectedItems"
                item-container-title="Selected Templates"
                :is-initializing="isLoading"
                :items="availableItems"
                :selected-items="selectedItems"
                :selected-item-cell-config="selectedItemCellConfig"
                :table-config="tableConfig"
                @removed-item="handleRemovedItem"
                @remove-all="handleRemoveAll"
                @selected-item="handleSelectedItem"
                @query="handleQuery"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Back</app-button>
            </div>
            <div class="action-container">
                <app-button :is-loading="isLoading" @click="add"> Add </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { LandingPageTemplateBoardServiceMixin } from '@/mixins/board/landing-page-template-board-service-mixin';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SelectorTable = () => import('@/components/selector-table/selector-table');

export default {
    name: 'landing-page-template-select-widget',

    components: {
        'app-button': AppButton,
        'selector-table': SelectorTable,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [LandingPageTemplateBoardServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,

            items: [],

            selectedItems: [],

            selectedItemCellConfig: {
                type: 'landing-page-template-element',
            },

            tableConfig: [
                {
                    cellWidth: '500px',
                    id: 1,
                    label: 'Name',
                    type: 'landing-page-template-element',
                    customHeaderStyles: {
                        justifyContent: 'left',
                        marginLeft: '130px',
                    },
                },
            ],
        };
    },

    computed: {
        availableItems() {
            return this.items.reduce((acc, item) => {
                if (
                    !(
                        this.selectedItems.length > 0 &&
                        this.selectedItems.some(({ id }) => id === item.id)
                    )
                ) {
                    acc.push(item);
                }
                return acc;
            }, []);
        },

        initialSelectedItems() {
            return this.widget.initialSelectedIds;
        },
    },

    created() {
        const initializeData = () => {
            return new Promise((resolve, reject) =>
                this.getData()
                    .then(() => {
                        if (this.initialSelectedItems.length) {
                            this.setInitialSelected(this.initialSelectedItems);
                        }
                        resolve();
                    })
                    .catch(reject),
            );
        };

        this.withLoadingBoundary(initializeData);
    },

    methods: {
        asTableItem(item) {
            return {
                id: item.id,
                name: item.template.name,
                thumbnailImageUrl: item.template.thumbnailImageUrl || null,
            };
        },

        handleQuery(options) {
            this.withLoadingBoundary(() => this.getData(options));
        },

        getData(options = {}) {
            this.items = [];
            return new Promise((resolve, reject) => {
                this.dispatchGetLandingPageTemplates({ options })
                    .then(({ items, count }) => {
                        this.items = items.map(this.asTableItem);
                        this.totalItems = count;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your landing page templates.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        setInitialSelected() {
            this.selectedItems = this.items.filter(({ id }) =>
                this.initialSelectedItems.includes(id),
            );
        },

        handleRemoveAll() {
            this.selectedItems.splice(0, this.selectedItems.length);
        },

        handleRemovedItem(item) {
            this.selectedItems = this.selectedItems.filter(({ id }) => id !== item.id);
        },

        handleSelectedItem(item) {
            if (this.selectedItems.every(({ id }) => id !== item.id)) {
                this.selectedItems.push(item);
            }
        },

        add() {
            EventBus.$emit(
                'landing-page-template:select',
                this.selectedItems.map((item) => item.id),
            );
            this.closeWidget();
        },

        withLoadingBoundary(promise) {
            this.isLoading = true;
            promise().finally(() => {
                this.isLoading = false;
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.landing-page-template-select-widget .saving-step {
    text-align: center;
}

.landing-page-template-select-widget .content-wrapper {
    padding: 17px !important;
}

.landing-page-template-select-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    border: 2px solid #cac6cb;
    color: #382153;
}

.landing-page-template-select-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}
</style>
